'use client';

/**
 * Family / Guardian Access — useFamilyContext hook (Unit U4).
 *
 * REQUIREMENTS SUMMARY (U4 — Active family context + Switch Family):
 * - Thin accessor over FamilyContext so pages/components read the active family
 *   without importing the context object directly. Exposes the server-resolved
 *   activeFamilyAccountId, families[], activeFamilyAthleteIds, isPrimaryGuardian,
 *   canManageEnrollments (the viewer's active-family enrollment permission),
 *   effectivePermissions, requiresSelection, and switchFamily() (R11/R12/R13).
 * - GATING CONTRACT: `canManageEnrollments` defaults to false while inert (flag
 *   off / guest / no active family), so downstream pickers must disable athletes
 *   ONLY when `enabled === true` AND `activeFamilyAccountId != null` AND the
 *   athlete is in `activeFamilyAthleteIds` AND `canManageEnrollments === false`.
 *   Never gate on `canManageEnrollments` alone — flag-off must stay neutral.
 * - SAFE OUTSIDE THE PROVIDER: because FamilyProvider is always mounted (it self-
 *   gates to inert while the flag is off), the default context value is the inert
 *   shape. Reading this hook before the provider mounts simply yields the inert
 *   value — there is no throw — so flag-off pages can call it unconditionally and
 *   stay behavior-neutral.
 *
 * DRY: re-exports the context value type; no logic lives here.
 */

import { useContext } from 'react';
import { FamilyContext } from '@/context/FamilyContext';

export function useFamilyContext() {
  return useContext(FamilyContext);
}

export default useFamilyContext;
